import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '../../utils/cn'
import Typography from '@Components/typo'

type Variant = 'primary' | 'error' | 'disabled' | null | undefined
const inputVariants = cva(
  'flex w-full rounded-2xl bg-input-default pl-[12px] pr-[4px] text-base placeholder:text-muted-foreground placeholder:text-content-placeholder text-content-default',
  {
    variants: {
      type: {
        textarea: 'rounded-2xl h-[100px] p-3',
        text: 'rounded-text-input h-[44px] text-ellipsis overflow-hidden',
        number: 'rounded-text-input h-[44px] w-full',
      },
      variant: {
        primary:
          'focus-visible:border-2 focus-visible:border-stroke-focused focus-visible:bg-input-default focus-visible:outline-none focus:ring-0 hover:bg-input-hover',
        error:
          'border-2 border-negative focus-visible:outline-none focus:ring-0 focus-visible:bg-input-default hover:bg-input-hover',
        disabled:
          'disabled:cursor-not-allowed bg-input-disabled text-muted-foreground',
      },
    },
    defaultVariants: {
      variant: 'primary',
      type: 'text',
    },
  }
)

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement | HTMLTextAreaElement>,
    VariantProps<typeof inputVariants> {
  label?: string
  isShowOptionalLabel?: boolean
  error?: string
  placeholder?: string
  rightIcon?: React.ReactNode
  leftIcon?: React.ReactNode
  type?: 'textarea' | 'text' | 'number'
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      type = 'text',
      label,
      isShowOptionalLabel,
      error,
      disabled,
      leftIcon,
      rightIcon,
      ...props
    },
    ref
  ) => {
    let variant: Variant = 'primary'

    if (error) {
      variant = 'error'
    }
    if (disabled) {
      variant = 'disabled'
    }
    return (
      <div className='flex w-full flex-col gap-2'>
        {label && (
          <label
            htmlFor={props.id}
            className='text-muted-foreground block text-sm font-medium'
          >
            <Typography element='span' size='base' color='text-content-default'>
              {label}
            </Typography>
            {isShowOptionalLabel && (
              <Typography element='span' size='base' color='text-content-hint'>
                {' '}
                (optional)
              </Typography>
            )}
          </label>
        )}
        <div className='relative flex w-full'>
          {leftIcon && (
            <div className='absolute left-2 flex h-full items-center'>
              {leftIcon}
            </div>
          )}
          {type === 'textarea' ? (
            <textarea
              disabled={disabled}
              className={cn(
                inputVariants({
                  variant: variant,
                  type,
                  className: `${leftIcon ? 'pl-10' : ''} ${className}`,
                })
              )}
              {...props}
            />
          ) : (
            <input
              type={type}
              disabled={disabled}
              className={cn(
                inputVariants({
                  variant: variant,
                  type,
                  className: `${leftIcon ? 'pl-10' : ''} ${className}`,
                })
              )}
              ref={ref}
              {...props}
            />
          )}

          {rightIcon && (
            <div className='absolute bottom-0 right-2 top-1'>{rightIcon}</div>
          )}
        </div>
        {error && (
          <Typography element='span' size='small' color='text-negative'>
            {error}
          </Typography>
        )}
      </div>
    )
  }
)
Input.displayName = 'Input'

export { Input }
